/**
 * Research Interview Service
 *
 * Handles API calls to the Node.js backend for research interview functionality.
 * Note: This calls the backend only - FastAPI is not exposed publicly.
 */

import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const researchApi = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' }
});

const researchService = {
  /**
   * Validate that a survey exists and is active
   * @param {string} surveyId - The survey identifier
   * @returns {Promise<Object>} Survey info including title, description, phases count
   */
  validateSurvey: async (surveyId) => {
    const response = await researchApi.get(`/api/research/surveys/${surveyId}`);
    return response.data;
  },

  /**
   * Start a new interview session
   * @param {string} surveyId - The survey identifier
   * @param {Object} userMetadata - Optional user information (name, email, company, role)
   * @returns {Promise<Object>} Session info including session_id and initial message
   */
  startInterview: async (surveyId, userMetadata = null) => {
    const response = await researchApi.post('/api/research/interviews/start', {
      survey_id: surveyId,
      user_metadata: userMetadata
    });
    return response.data;
  },

  /**
   * Send a message in an interview
   * @param {string} sessionId - The session identifier
   * @param {string} message - User's message
   * @returns {Promise<Object>} AI response with progress info
   */
  sendMessage: async (sessionId, message) => {
    const response = await researchApi.post(`/api/research/interviews/${sessionId}/chat`, {
      message
    });
    return response.data;
  },

  /**
   * Get the current status of an interview
   * @param {string} sessionId - The session identifier
   * @returns {Promise<Object>} Interview status including completion state and progress
   */
  getStatus: async (sessionId) => {
    const response = await researchApi.get(`/api/research/interviews/${sessionId}/status`);
    return response.data;
  },

  /**
   * Get the full transcript of an interview
   * @param {string} sessionId - The session identifier
   * @returns {Promise<Object>} Transcript history
   */
  getTranscript: async (sessionId) => {
    const response = await researchApi.get(`/api/research/interviews/${sessionId}/transcript`);
    return response.data;
  }
};

export default researchService;
